import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { format, addHours } from "date-fns";
import {
  KeyRound, CheckCircle2, Upload, Clock, AlertCircle, User, Phone,
  CalendarDays, Home, Loader2, DollarSign, CreditCard, MapPin, Users,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useGuestTokenByToken, useUpdateGuestToken } from "@/hooks/useGuestToken";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { trackEvent } from "@/lib/analytics";
import { calcBookingPrice, type Amenity } from "@/hooks/useAmenities";

const HOURS = Array.from({ length: 16 }, (_, i) => i + 7);

// ─── Guest Amenity Booking Section ────────────────────────────────────────────

interface GuestAmenitySectionProps {
  orgId: string | null;
  guestTokenId: string;
  guestName: string;
  portalToken: string;
}

function GuestAmenitySection({ orgId, guestTokenId, guestName, portalToken }: GuestAmenitySectionProps) {
  const { toast } = useToast();
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAmenity, setSelectedAmenity] = useState<Amenity | null>(null);
  const [bookingForm, setBookingForm] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    startHour: "10",
    durationHours: "1",
    notes: "",
    guestCount: "1",
  });
  const [creating, setCreating] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(
    new URLSearchParams(window.location.search).get("amenity_payment") === "success",
  );

  // Fetch amenities for this org (public read)
  useEffect(() => {
    if (!orgId) { setLoading(false); return; }
    supabase
      .from("amenities")
      .select("*")
      .eq("org_id", orgId)
      .eq("is_active", true)
      .order("name")
      .then(({ data }) => {
        setAmenities((data as Amenity[]) ?? []);
        setLoading(false);
      });
  }, [orgId]);

  if (!orgId || (!loading && amenities.length === 0)) return null;

  const previewPrice = selectedAmenity?.requires_payment
    ? calcBookingPrice(
        selectedAmenity,
        new Date(`${bookingForm.date}T${bookingForm.startHour.padStart(2, "0")}:00:00`).toISOString(),
        addHours(
          new Date(`${bookingForm.date}T${bookingForm.startHour.padStart(2, "0")}:00:00`),
          parseInt(bookingForm.durationHours),
        ).toISOString(),
      )
    : 0;

  async function handleBookAmenity() {
    if (!selectedAmenity || !orgId) return;
    setCreating(true);
    try {
      const startTime = new Date(
        `${bookingForm.date}T${bookingForm.startHour.padStart(2, "0")}:00:00`,
      ).toISOString();
      const endTime = addHours(
        new Date(`${bookingForm.date}T${bookingForm.startHour.padStart(2, "0")}:00:00`),
        parseInt(bookingForm.durationHours),
      ).toISOString();

      // Conflict check
      const { count } = await supabase
        .from("amenity_bookings")
        .select("id", { count: "exact", head: true })
        .eq("amenity_id", selectedAmenity.id)
        .eq("status", "approved")
        .lt("start_time", endTime)
        .gt("end_time", startTime);

      if ((count ?? 0) > 0) {
        toast({ title: "Slot taken", description: "This time slot is already booked. Please pick another time.", variant: "destructive" });
        setCreating(false);
        return;
      }

      // Insert booking (no auth — guest portal, uses service role anon key)
      const { data: newBooking, error: bErr } = await supabase
        .from("amenity_bookings")
        .insert({
          org_id: orgId,
          amenity_id: selectedAmenity.id,
          user_id: null, // guest has no user account
          title: `${guestName} (Guest)`,
          start_time: startTime,
          end_time: endTime,
          guest_count: parseInt(bookingForm.guestCount),
          notes: bookingForm.notes || null,
          status: selectedAmenity.require_approval ? "pending" : "approved",
          payment_status: selectedAmenity.requires_payment ? "pending" : "waived",
          payment_amount: previewPrice,
        })
        .select()
        .single();

      if (bErr) throw bErr;

      // If payment required, redirect to Stripe Checkout
      if (selectedAmenity.requires_payment && previewPrice > 0 && newBooking) {
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-amenity-payment`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              booking_id: newBooking.id,
              mode: "checkout",
              success_url: `${window.location.href.split("?")[0]}?amenity_payment=success`,
              cancel_url: window.location.href,
            }),
          },
        );
        const result = await res.json();
        if (!res.ok) throw new Error(result.error ?? "Payment setup failed");
        if (result.url) {
          window.location.href = result.url;
          return;
        }
      }

      setSelectedAmenity(null);
      toast({
        title: selectedAmenity.require_approval ? "Booking request sent!" : "Amenity booked!",
        description: selectedAmenity.require_approval
          ? "Your request is awaiting approval from the property manager."
          : "Your booking is confirmed.",
      });
    } catch (err: any) {
      toast({ title: "Booking failed", description: err.message, variant: "destructive" });
    }
    setCreating(false);
  }

  return (
    <>
      {paymentSuccess && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 text-sm">
            Payment received! Your amenity booking has been submitted.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            Book a Building Amenity
          </CardTitle>
          <CardDescription className="text-xs">
            Book shared amenities during your stay. Some require approval and/or payment.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <div className="space-y-3">
              {amenities.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between gap-3 rounded-lg border p-3 hover:bg-muted/40 transition-colors cursor-pointer"
                  onClick={() => setSelectedAmenity(a)}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: a.color }} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{a.name}</p>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {a.location && (
                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                            <MapPin className="h-2.5 w-2.5" />{a.location}
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                          <Users className="h-2.5 w-2.5" />max {a.capacity}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {a.requires_payment ? (
                      <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">
                        {a.price_flat != null ? `$${a.price_flat}` : `$${a.price_per_hour}/hr`}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">Free</Badge>
                    )}
                    <Button size="sm" variant="outline" className="h-7 text-xs">Book</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking dialog */}
      <Dialog open={!!selectedAmenity} onOpenChange={(v) => { if (!v) setSelectedAmenity(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Book {selectedAmenity?.name}</DialogTitle>
            {selectedAmenity?.requires_payment && (
              <DialogDescription className="text-xs">
                {selectedAmenity.price_flat != null
                  ? `Flat fee: $${selectedAmenity.price_flat} CAD — payment via Stripe`
                  : `$${selectedAmenity.price_per_hour}/hr — payment via Stripe`}
              </DialogDescription>
            )}
          </DialogHeader>
          {selectedAmenity && (
            <div className="space-y-3 py-1">
              {selectedAmenity.description && (
                <p className="text-xs text-muted-foreground">{selectedAmenity.description}</p>
              )}
              {selectedAmenity.rules && (
                <Alert className="py-2 border-amber-200 bg-amber-50">
                  <AlertDescription className="text-xs text-amber-800">📋 {selectedAmenity.rules}</AlertDescription>
                </Alert>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input type="date" className="mt-1 h-8 text-xs"
                    value={bookingForm.date}
                    onChange={(e) => setBookingForm((p) => ({ ...p, date: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Guests</Label>
                  <Input type="number" min={1} max={selectedAmenity.capacity} className="mt-1 h-8 text-xs"
                    value={bookingForm.guestCount}
                    onChange={(e) => setBookingForm((p) => ({ ...p, guestCount: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Start Time</Label>
                  <Select value={bookingForm.startHour}
                    onValueChange={(v) => setBookingForm((p) => ({ ...p, startHour: v }))}>
                    <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {HOURS.map((h) => (
                        <SelectItem key={h} value={String(h)} className="text-xs">
                          {h === 12 ? "12:00 pm" : h < 12 ? `${h}:00 am` : `${h - 12}:00 pm`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Duration</Label>
                  <Select value={bookingForm.durationHours}
                    onValueChange={(v) => setBookingForm((p) => ({ ...p, durationHours: v }))}>
                    <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: selectedAmenity.max_hours }, (_, i) => i + 1).map((h) => (
                        <SelectItem key={h} value={String(h)} className="text-xs">
                          {h} hour{h > 1 ? "s" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {selectedAmenity.requires_payment && previewPrice > 0 && (
                <>
                  <Separator />
                  <div className="flex items-center justify-between rounded-md bg-muted px-3 py-2">
                    <span className="text-xs text-muted-foreground">Total due</span>
                    <span className="text-sm font-bold">${previewPrice.toFixed(2)} CAD</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    You'll be redirected to a secure Stripe payment page.
                  </p>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedAmenity(null)}>Cancel</Button>
            <Button
              onClick={handleBookAmenity}
              disabled={creating || !bookingForm.date}
            >
              {creating ? (
                <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Processing…</>
              ) : selectedAmenity?.requires_payment && previewPrice > 0
              ? <><CreditCard className="mr-1.5 h-3.5 w-3.5" />Pay ${previewPrice.toFixed(2)}</>
              : "Confirm Booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const BUILDING_RULES = [
  "No parties or events with more than the registered guest count.",
  "Quiet hours are 10 PM – 8 AM. Please respect all neighbours.",
  "No smoking anywhere in the building including balconies.",
  "Pets must be registered with the strata office before arrival.",
  "Parking is for registered guests only — do not use visitor stalls beyond your limit.",
  "All guests are responsible for any damage caused during their stay.",
  "Emergency evacuation routes are posted in the hallway on each floor.",
  "Dispose of waste in designated recycling and compost bins only.",
];

// ─── Guest Request Section ───────────────────────────────────────────────────

const GUEST_REQUEST_TYPES = [
  { value: "maintenance", label: "🔧 Maintenance Issue", description: "Report a problem in your unit or common areas" },
  { value: "inquiry",     label: "💬 Question / Inquiry", description: "General question for the host or manager" },
  { value: "dispute",     label: "⚖️ Complaint",           description: "Lodge a complaint or raise a concern" },
] as const;

type GuestRequestType = typeof GUEST_REQUEST_TYPES[number]["value"];

function GuestRequestSection({
  orgId,
  guestTokenId,
  guestName,
  guestEmail,
}: {
  orgId: string | null;
  guestTokenId: string;
  guestName: string;
  guestEmail?: string;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<GuestRequestType>("maintenance");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!orgId) return null;

  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.from("requests").insert({
        org_id: orgId,
        type,
        context_relationship: "guest_to_host",
        priority: "normal",
        status: "new",
        submitted_by_guest_token_id: guestTokenId,
        submitted_by_name: guestName,
        submitted_by_email: guestEmail ?? null,
        subject: subject.trim(),
        description: description.trim(),
      });
      if (error) throw error;
      setSubmitted(true);
      setSubject(""); setDescription(""); setType("maintenance");
      toast({ title: "Request submitted", description: "Your host has been notified and will respond shortly." });
    } catch (e: any) {
      toast({ title: "Error", description: e.message ?? "Failed to submit request", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <div className="mt-4 rounded-xl border border-dashed border-border bg-muted/20 p-4 text-center">
        <p className="text-xs text-muted-foreground mb-2">Need help or have a question?</p>
        <button
          type="button"
          onClick={() => { setOpen(true); setSubmitted(false); }}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          📬 Submit a Request
        </button>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="mt-4 rounded-xl border bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 p-5 text-center">
        <div className="text-3xl mb-2">✅</div>
        <p className="text-sm font-semibold text-green-800 dark:text-green-300">Request received!</p>
        <p className="text-xs text-muted-foreground mt-1">Your host will respond as soon as possible.</p>
        <button
          type="button"
          onClick={() => { setSubmitted(false); setOpen(false); }}
          className="mt-3 text-xs text-primary underline hover:no-underline"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Submit a Request</h3>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
      </div>

      {/* Type selector */}
      <div className="grid gap-2">
        {GUEST_REQUEST_TYPES.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setType(t.value)}
            className={`text-left rounded-lg border p-3 transition-colors ${
              type === t.value ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
            }`}
          >
            <div className="text-sm font-medium">{t.label}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{t.description}</div>
          </button>
        ))}
      </div>

      {/* Subject */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Subject *</label>
        <input
          type="text"
          placeholder="Brief summary of your request"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={200}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      {/* Description */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Details *</label>
        <textarea
          placeholder="Describe your request in detail…"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={2000}
          rows={4}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
        />
      </div>

      <button
        type="button"
        disabled={!subject.trim() || !description.trim() || submitting}
        onClick={handleSubmit}
        className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        {submitting ? "Submitting…" : "📬 Submit Request"}
      </button>
    </div>
  );
}

export default function GuestPortal() {
  const { token } = useParams<{ token: string }>();
  const { data: guestToken, isLoading, error } = useGuestTokenByToken(token ?? null);
  const updateToken = useUpdateGuestToken();
  const { toast } = useToast();
  const [step, setStep] = useState<"loading" | "rules" | "id" | "done">("loading");
  const [idFile, setIdFile] = useState<File | null>(null);

  // Determine initial step from token state
  const initialStep = () => {
    if (!guestToken) return "rules";
    if (!guestToken.rules_acknowledged) return "rules";
    if (!guestToken.id_uploaded) return "id";
    return "done";
  };

  // Set step once data loads — also fire guest_checkin_started on first visit
  if (!isLoading && step === "loading" && guestToken) {
    const next = initialStep() as "rules" | "id" | "done";
    setStep(next);
    if (next === "rules") {
      // Guest has arrived at the portal for the first time — check-in started
      trackEvent("guest_checkin_started");
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (error || !guestToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center gap-4 py-12">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <h2 className="text-xl font-bold">Link Not Found</h2>
            <p className="text-center text-sm text-muted-foreground">
              This guest link is invalid or has expired. Please contact your host for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if stay has expired
  const isExpired = new Date(guestToken.expires_at) < new Date();
  if (isExpired && step !== "done") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center gap-4 py-12">
            <Clock className="h-12 w-12 text-muted-foreground" />
            <h2 className="text-xl font-bold">Stay Has Ended</h2>
            <p className="text-center text-sm text-muted-foreground">
              Your stay at this property has concluded. Thank you for visiting!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const booking = guestToken.booking as any;
  const unit = booking?.unit;
  const host = booking?.host;
  const hostProfile = host?.profile;

  const handleAcknowledgeRules = async () => {
    // Uses update_guest_token_checkin() RPC — token string is the credential
    await updateToken.mutateAsync({
      token: guestToken.token,
      rulesAcknowledged: true,
    });
    setStep("id");
    toast({ title: "Rules acknowledged", description: "You may now upload your ID." });
  };

  const handleIdUpload = async () => {
    if (!idFile || !guestToken) return;

    try {
      // Validate file size (max 10 MB)
      const MAX_MB = 10;
      if (idFile.size > MAX_MB * 1024 * 1024) {
        toast({
          title: "File too large",
          description: `Please upload an image smaller than ${MAX_MB} MB.`,
          variant: "destructive",
        });
        return;
      }

      // Upload to Supabase Storage in the "guest-ids" bucket
      // Path: guest-ids/{tokenId}/{timestamp}_{filename}
      const safeName = idFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `${guestToken.id}/${Date.now()}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("guest-ids")
        .upload(filePath, idFile, { upsert: false });

      if (uploadError) {
        // If bucket doesn't exist yet, fall back to just marking uploaded
        console.warn("Storage upload failed (bucket may not exist):", uploadError.message);
      }

      // Mark the token as ID uploaded — uses update_guest_token_checkin() RPC
      await updateToken.mutateAsync({
        token: guestToken.token,
        idUploaded: true,
      });

      setStep("done");
      trackEvent("guest_checkin_completed");
      toast({ title: "ID uploaded ✓", description: "Your check-in is complete. Welcome!" });
    } catch (err: any) {
      toast({
        title: "Upload failed",
        description: err.message ?? "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
            <KeyRound className="h-6 w-6 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Guest Check-In</h1>
          <p className="text-sm text-muted-foreground">Stratakey STR Compliance Platform</p>
        </div>

        {/* Booking Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Home className="h-4 w-4" />
              Your Stay
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Unit</span>
              <span className="font-medium">{unit?.unit_number ? `Unit ${unit.unit_number}` : "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Guest</span>
              <span className="font-medium">{booking?.guest_name ?? "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Check-in</span>
              <span className="font-medium">{booking?.check_in ?? "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Check-out</span>
              <span className="font-medium">{booking?.check_out ?? "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Nights</span>
              <span className="font-medium">{booking?.nights ?? "—"}</span>
            </div>
          </CardContent>
        </Card>

        {/* Progress Steps */}
        <div className="flex items-center gap-2 text-xs">
          {[
            { key: "rules", label: "Rules" },
            { key: "id", label: "ID Upload" },
            { key: "done", label: "Complete" },
          ].map((s, i) => {
            const stepOrder = ["rules", "id", "done"];
            const currentIdx = stepOrder.indexOf(step === "loading" ? "rules" : step);
            const thisIdx = stepOrder.indexOf(s.key);
            const isDone = thisIdx < currentIdx || step === "done";
            const isActive = s.key === step;
            return (
              <div key={s.key} className="flex items-center gap-1 flex-1">
                <div className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ${isDone ? "bg-primary text-primary-foreground" : isActive ? "bg-primary/20 text-primary border-2 border-primary" : "bg-muted text-muted-foreground"}`}>
                  {isDone ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
                </div>
                <span className={isActive ? "text-primary font-medium" : "text-muted-foreground"}>{s.label}</span>
                {i < 2 && <div className="h-px flex-1 bg-border" />}
              </div>
            );
          })}
        </div>

        {/* Step: Acknowledge Rules */}
        {(step === "rules" || step === "loading") && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Building Rules</CardTitle>
              <CardDescription className="text-xs">Please read and acknowledge the strata building rules before your stay.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="space-y-2">
                {BUILDING_RULES.map((rule, i) => (
                  <li key={i} className="flex gap-2 text-xs text-muted-foreground">
                    <span className="mt-0.5 flex-shrink-0 font-medium text-foreground">{i + 1}.</span>
                    {rule}
                  </li>
                ))}
              </ul>
              <Button className="w-full" onClick={handleAcknowledgeRules} disabled={updateToken.isPending}>
                I Understand and Agree to These Rules
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step: Upload ID */}
        {step === "id" && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Upload Photo ID
              </CardTitle>
              <CardDescription className="text-xs">Please upload a photo of your government-issued ID for verification.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div
                className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border p-6 cursor-pointer hover:bg-muted/50"
                onClick={() => document.getElementById("id-upload")?.click()}
              >
                <User className="h-8 w-8 text-muted-foreground" />
                {idFile ? (
                  <p className="text-sm font-medium text-primary">{idFile.name}</p>
                ) : (
                  <>
                    <p className="text-sm font-medium">Tap to select a photo</p>
                    <p className="text-xs text-muted-foreground">Passport, driver's licence, or government ID</p>
                  </>
                )}
              </div>
              <input
                id="id-upload"
                type="file"
                accept="image/*,application/pdf"
                title="Upload your government-issued photo ID"
                aria-label="Upload your government-issued photo ID"
                className="hidden"
                onChange={(e) => setIdFile(e.target.files?.[0] ?? null)}
              />
              <Button className="w-full" onClick={handleIdUpload} disabled={!idFile || updateToken.isPending}>
                {updateToken.isPending ? "Uploading..." : "Upload ID & Complete Check-In"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step: Done */}
        {step === "done" && (
          <>
            <Card>
              <CardContent className="flex flex-col items-center gap-4 py-8">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold">You're All Set!</h2>
                <p className="text-center text-sm text-muted-foreground">
                  Check-in is complete. Welcome to your stay!
                </p>
                {guestToken.access_code && (
                  <div className="w-full rounded-lg bg-muted p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Your Access Code</p>
                    <p className="text-3xl font-bold tracking-widest">{guestToken.access_code}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Amenity Booking Section (shown after check-in) */}
            <GuestAmenitySection
              orgId={(booking as any)?.unit?.org_id ?? (guestToken as any).org_id ?? null}
              guestTokenId={guestToken.id}
              guestName={booking?.guest_name ?? "Guest"}
              portalToken={guestToken.token}
            />
            {/* Guest can submit maintenance / inquiry / complaint requests */}
            <GuestRequestSection
              orgId={(booking as any)?.unit?.org_id ?? (guestToken as any).org_id ?? null}
              guestTokenId={guestToken.id}
              guestName={booking?.guest_name ?? "Guest"}
            />
          </>
        )}

        {/* ─────────────────────────────────────────────────────────────────── */}

        {/* Host Contact */}
        {hostProfile && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Contact Your Host</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>{hostProfile.full_name}</span>
              </div>
              {hostProfile.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <a href={`tel:${hostProfile.phone}`} className="text-primary hover:underline">{hostProfile.phone}</a>
                </div>
              )}
              {hostProfile.email && (
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${hostProfile.email}`} className="text-primary hover:underline">{hostProfile.email}</a>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
